import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { playerDisplayName, type PlayerPublic, type SportPublic, type AgeGroupPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import { SchoolCrest } from "@/components/school-crest";
import { MultiSelectDropdown, type MultiSelectOption } from "@/components/multi-select-dropdown";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import {
  Plus, Pencil, Trash2, Upload, Download, GraduationCap, AlertTriangle, Users, ChevronRight,
} from "lucide-react";
import { SendMessageButton } from "@/components/send-message-button";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";
import { MobileFab, FilterChip, FilterChipRow } from "@/components/mobile-list-chrome";
import { useGridPreferences } from "@/lib/use-grid-preferences";

const PAGE_SIZE = 20;

export default function PlayersPage() {
  const { user: authUser } = useAuth();
  const schoolIds = useSchoolFilterIds();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("players");

  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const [selected, setSelected] = useState<PlayerPublic | null>(null);
  const [detailPlayer, setDetailPlayer] = useState<PlayerPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Reference data for facet options.
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [allAgeGroups, setAllAgeGroups] = useState<AgeGroupPublic[]>([]);

  const effectiveSchoolId = schoolIds[0] ?? "";
  const schoolIdsKey = schoolIds.join(",");
  const isUnscoped = schoolIds.length === 0;

  // --- Facet state (URL-persisted, separate from DataTable's q/sort/order/page) ---
  const activeSports = useMemo(() => {
    const param = searchParams.get("sports");
    return param ? new Set(param.split(",")) : new Set<string>();
  }, [searchParams]);
  const activeAgeGroups = useMemo(() => {
    const param = searchParams.get("age_groups");
    return param ? new Set(param.split(",")) : new Set<string>();
  }, [searchParams]);
  const indemnityFilter = searchParams.get("indemnity") as "complete" | "incomplete" | null;

  const sportsCsv = [...activeSports].join(",");
  const ageGroupsCsv = [...activeAgeGroups].join(",");

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }

  function toggleSport(sportId: string) {
    const next = new Set(activeSports);
    if (next.has(sportId)) next.delete(sportId); else next.add(sportId);
    updateParams({ sports: next.size > 0 ? [...next].join(",") : null, page: null });
  }
  function toggleAgeGroup(agId: string) {
    const next = new Set(activeAgeGroups);
    if (next.has(agId)) next.delete(agId); else next.add(agId);
    updateParams({ age_groups: next.size > 0 ? [...next].join(",") : null, page: null });
  }

  // Load reference data -- sports scoped to school when available, age groups always global.
  useEffect(() => {
    if (effectiveSchoolId) {
      api.getSchoolSports(effectiveSchoolId).then((r) => {
        setSports(r.sports.map((s) => ({ id: s.sport_id, name: s.sport_name, default_court_type_id: null, court_type_name: s.court_type_name ?? null, enabled: true })));
      }).catch(() => {});
    } else {
      api.getSports().then((r) => setSports(r.sports)).catch(() => {});
    }
    api.getAllAgeGroups().then((r) => setAllAgeGroups(r.age_groups)).catch(() => {});
  }, [effectiveSchoolId]);

  const sportOptions: MultiSelectOption[] = useMemo(() => sports.map((s) => ({ value: s.id, label: s.name })), [sports]);
  const ageGroupOptions: MultiSelectOption[] = useMemo(
    () => allAgeGroups.map((ag) => ({ value: ag.id, label: ag.name, group: ag.sport_name })),
    [allAgeGroups],
  );

  // --- Server-driven fetch ---
  // unscoped -> GET /players (search/sort/page only); single school -> GET
  // /schools/:id/players with facets server-side; multiple schools -> fetch each
  // (facets + search server-side) then merge + sort + paginate client-side.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: PlayerPublic[]; total: number }> => {
      if (isUnscoped) {
        const res = await api.getAllPlayers({
          page: params.page, per_page: params.per_page,
          search: params.search, sort: params.sort, order: params.order,
        });
        return { data: res.players, total: res.total };
      }
      const facets = {
        sport: sportsCsv || undefined,
        age_group: ageGroupsCsv || undefined,
        indemnity: indemnityFilter ?? undefined,
      };
      if (schoolIds.length === 1) {
        const res = await api.getPlayers(schoolIds[0], {
          ...facets,
          search: params.search, sort: params.sort, order: params.order,
          page: params.page, per_page: params.per_page,
        });
        return { data: res.players, total: res.total ?? res.players.length };
      }
      const merged = (
        await Promise.all(schoolIds.map((id) => api.getPlayers(id, { ...facets, search: params.search }).then((r) => r.players)))
      ).flat();
      return applyClientListParams(merged, params, {
        sortAccessors: {
          surname: (p) => p.surname ?? "",
          first_name: (p) => p.first_name ?? "",
          grade: (p) => p.grade ?? 0,
          player_code: (p) => p.player_code ?? "",
        },
        defaultSort: "surname",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [schoolIdsKey, sportsCsv, ageGroupsCsv, indemnityFilter, isUnscoped],
  );

  const fetchKey = [schoolIdsKey, sportsCsv, ageGroupsCsv, indemnityFilter ?? "", refreshTick].join("|");

  // Enrich selection with full detail (parents).
  useEffect(() => {
    if (!selected) { setDetailPlayer(null); return; }
    setDetailPlayer(selected);
    api.getPlayer(selected.id).then((res) => setDetailPlayer(res.player)).catch(() => {});
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(playerId: string) {
    if (!confirm("Are you sure you want to delete this player?")) return;
    try {
      await api.deletePlayer(playerId);
      if (selected?.id === playerId) { setSelected(null); setDetailPlayer(null); }
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete player");
    }
  }

  async function handleExport() {
    if (!effectiveSchoolId) return;
    try {
      const blob = await api.exportFamilyCodes(effectiveSchoolId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "family_codes.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to export codes");
    }
  }

  async function handleAdvanceGrades() {
    if (!effectiveSchoolId) return;
    if (!confirm("Advance grades for active players by 1 year? This cannot be undone.")) return;
    try {
      const result = await api.advanceGrades(effectiveSchoolId);
      setRefreshTick((t) => t + 1);
      alert(`${result.updated} player(s) advanced to next grade.`);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to advance grades");
    }
  }

  const columns: DataTableColumn<PlayerPublic>[] = [
    { key: "first_name", header: "First Name", sortable: true, className: "font-medium", render: (p) => p.first_name ?? "--" },
    { key: "surname", header: "Surname", sortable: true, className: "font-medium", render: (p) => p.surname ?? "--" },
    { key: "nickname", header: "Nickname", className: "text-muted-foreground", render: (p) => p.nickname ?? "--" },
    { key: "date_of_birth", header: "Date of Birth", className: "text-muted-foreground", render: (p) => p.date_of_birth },
    { key: "grade", header: "Grade", sortable: true, className: "text-muted-foreground", render: (p) => (p.grade !== null ? `Gr ${p.grade}` : "--") },
    {
      key: "player_code", header: "Player Code", sortable: true, className: "text-muted-foreground",
      render: (p) => p.player_code ? <code className="rounded bg-muted px-2 py-0.5 text-sm">{p.player_code}</code> : <span className="text-muted-foreground">--</span>,
    },
    { key: "enrolled_sports", header: "Enrolled Sports", render: (p) => renderEnrollments(p) },
    {
      key: "family_code", header: "Family Code",
      render: (p) => p.family_code ? <code className="rounded bg-muted px-2 py-0.5 text-sm">{p.family_code}</code> : <span className="text-muted-foreground">--</span>,
    },
    {
      key: "active", header: "Active",
      render: (p) => (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${p.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
          {p.active ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "actions", header: "", headerClassName: "w-24", cardRole: "action",
      render: (player) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {player._actions.includes("send_message") && (
            <SendMessageButton contextType="player" contextId={player.id} contextLabel={playerDisplayName(player.first_name, player.surname)} />
          )}
          {player._actions.includes("edit") && (
            <Link to={`/players/${player.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="edit">
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {player._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(player.id)} data-action="delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Bespoke mobile player card: initials avatar in school colour, name,
  // grade/sport sub, indemnity pill, chevron. Links to edit when permitted.
  const renderPlayerCard = (p: PlayerPublic) => {
    const name = playerDisplayName(p.first_name, p.surname);
    const initials = ((p.first_name?.[0] ?? "") + (p.surname?.[0] ?? "")).toUpperCase() || "?";
    const sportsText = [...new Set(p.enrollments.map((e) => e.sport_name))].join(" · ");
    const grade = p.grade !== null ? `Gr ${p.grade}` : null;
    const sub = [grade, sportsText].filter(Boolean).join(" · ");
    const indemnityIncomplete = p.enrollments.some((e) => !e.indemnity_complete);
    const editable = p._actions?.includes("edit") ?? false;
    const inner = (
      <>
        <span
          style={{ backgroundColor: p.school_primary_color || "#3b82f6" }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          aria-hidden
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{name}</p>
          {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
        </div>
        {indemnityIncomplete && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            Indemnity
          </span>
        )}
        {editable && <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" />}
      </>
    );
    const cls = "flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm";
    return editable ? (
      <Link to={`/players/${p.id}/edit`} className={`${cls} transition-transform active:scale-[.99]`}>
        {inner}
      </Link>
    ) : (
      <div className={cls}>{inner}</div>
    );
  };

  return (
    <div className="flex h-full gap-0" data-page="players-list" data-state={tableState}>
    <div className="min-w-0 flex-1 flex flex-col gap-6">
      <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Players</h1>
          <p className="text-muted-foreground">Manage players and family codes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {effectiveSchoolId && canManage && (
            <>
              <Button variant="outline" size="sm" onClick={handleAdvanceGrades}>
                <GraduationCap className="mr-2 h-4 w-4" /> Advance Grades
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" /> Export Codes
              </Button>
              <Link to={`/schools/${effectiveSchoolId}/players/import`} className={buttonVariants({ variant: "outline", size: "sm" })}>
                <Upload className="mr-2 h-4 w-4" /> Import CSV
              </Link>
              <Link to={`/schools/${effectiveSchoolId}/players/unassigned`} className={buttonVariants({ variant: "outline", size: "sm" })} data-action="unassigned">
                <Users className="mr-2 h-4 w-4" /> Unassigned
              </Link>
              <Link to={`/schools/${effectiveSchoolId}/register-player`} className={buttonVariants({ variant: "outline", size: "sm" })} data-action="register">
                <Plus className="mr-2 h-4 w-4" /> Register (full)
              </Link>
              <Link to={`/schools/${effectiveSchoolId}/players/new`} className={buttonVariants({ size: "sm" })} data-action="create">
                <Plus className="mr-2 h-4 w-4" /> Add Player
              </Link>
            </>
          )}
          <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
        </div>
      </div>

      {/* Filter bar (school scope + facets). Search/sort/pagination live in the table. */}
      <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center" data-region="filters">
        <SchoolFilter />
        {!isUnscoped && (
          <div className="flex flex-wrap gap-1.5">
            <MultiSelectDropdown
              label="Sports" options={sportOptions} selected={activeSports}
              onToggle={toggleSport} onClear={() => updateParams({ sports: null, page: null })}
              data-field="sports"
            />
            <MultiSelectDropdown
              label="Age Groups" options={ageGroupOptions} selected={activeAgeGroups}
              onToggle={toggleAgeGroup} onClear={() => updateParams({ age_groups: null, page: null })}
              data-field="age-groups"
            />
            <Button variant={!indemnityFilter ? "default" : "outline"} size="sm" onClick={() => updateParams({ indemnity: null, page: null })}>All</Button>
            <Button variant={indemnityFilter === "complete" ? "default" : "outline"} size="sm" onClick={() => updateParams({ indemnity: indemnityFilter === "complete" ? null : "complete", page: null })}>Indemnity Complete</Button>
            <Button variant={indemnityFilter === "incomplete" ? "default" : "outline"} size="sm" onClick={() => updateParams({ indemnity: indemnityFilter === "incomplete" ? null : "incomplete", page: null })}>Indemnity Incomplete</Button>
          </div>
        )}
      </div>

      <div data-region="results">
        <DataTable
          columns={columns}
          fetcher={fetcher}
          fetchKey={fetchKey}
          rowKey={(p) => p.id}
          rowProps={(p) => ({ "data-item": "player", "data-item-id": p.id })}
          rowClassName={(p) => (selected?.id === p.id ? "bg-muted" : undefined)}
          searchPlaceholder="Search players..."
          defaultSort="surname"
          pageSize={PAGE_SIZE}
          emptyMessage="No players match your filters."
          onRowClick={(p) => setSelected(p)}
          onStateChange={setTableState}
          renderCard={renderPlayerCard}
          toolbarExtra={
            !isUnscoped && (
              <FilterChipRow className="md:hidden">
                <FilterChip
                  active={!indemnityFilter && activeSports.size === 0 && activeAgeGroups.size === 0}
                  onClick={() => updateParams({ sports: null, age_groups: null, indemnity: null, page: null })}
                >
                  All
                </FilterChip>
                <FilterChip
                  active={indemnityFilter === "incomplete"}
                  onClick={() => updateParams({ indemnity: indemnityFilter === "incomplete" ? null : "incomplete", page: null })}
                >
                  <span className="inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Indemnity
                  </span>
                </FilterChip>
                {sportOptions.map((s) => (
                  <FilterChip key={s.value} active={activeSports.has(s.value)} onClick={() => toggleSport(s.value)}>
                    {s.label}
                  </FilterChip>
                ))}
                {ageGroupOptions.map((a) => (
                  <FilterChip key={a.value} active={activeAgeGroups.has(a.value)} onClick={() => toggleAgeGroup(a.value)}>
                    {a.label}
                  </FilterChip>
                ))}
              </FilterChipRow>
            )
          }
        />
      </div>
    </div>
    {effectiveSchoolId && canManage && <MobileFab to={`/schools/${effectiveSchoolId}/players/new`} label="Add player" />}
    <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Player">
      {detailPlayer && (
        <>
          <PreviewField label="Name">{playerDisplayName(detailPlayer.first_name, detailPlayer.surname)}</PreviewField>
          {detailPlayer.nickname && <PreviewField label="Nickname">{detailPlayer.nickname}</PreviewField>}
          <PreviewField label="Date of Birth">{detailPlayer.date_of_birth}</PreviewField>
          <PreviewField label="Grade">{detailPlayer.grade !== null ? `Grade ${detailPlayer.grade}` : "--"}</PreviewField>
          {detailPlayer.player_code && <PreviewField label="Player Code">{detailPlayer.player_code}</PreviewField>}
          <PreviewField label="Family Code">{detailPlayer.family_code ?? "--"}</PreviewField>
          <PreviewField label="School">
            <span className="inline-flex items-center gap-2">
              <SchoolCrest name={detailPlayer.school_name} logoUrl={detailPlayer.school_logo_url} primaryColor={detailPlayer.school_primary_color} size={20} />
              {detailPlayer.school_name}
            </span>
          </PreviewField>
          <PreviewField label="Active">{detailPlayer.active ? "Yes" : "No"}</PreviewField>
          {detailPlayer.enrollment_year && <PreviewField label="Enrollment Year">{detailPlayer.enrollment_year}</PreviewField>}

          {detailPlayer.enrollments.length > 0 && (
            <div className="flex flex-col gap-1.5 border-t border-border pt-3 mt-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sport Enrollments</span>
              {detailPlayer.enrollments.map((e) => (
                <div key={e.sport_id} className="flex items-center justify-between text-sm">
                  <span>{e.sport_name}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${e.indemnity_complete ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {!e.indemnity_complete && <AlertTriangle className="h-3 w-3" />}
                    {e.indemnity_complete ? "Indemnity" : "No indemnity"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {detailPlayer.parents.length > 0 && (
            <div className="flex flex-col gap-1.5 border-t border-border pt-3 mt-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Parents</span>
              {detailPlayer.parents.map((p) => (
                <div key={p.user_id} className="text-sm">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.email}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PreviewPane>
    </div>
  );
}

// Enrollment chips (sport + indemnity status + age-group override badge).
function renderEnrollments(player: PlayerPublic) {
  if (player.enrollments.length === 0) return <span className="text-muted-foreground">--</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {player.enrollments.map((e) => (
        <span key={e.sport_id} className="inline-flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${e.indemnity_complete ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
            {!e.indemnity_complete && <AlertTriangle className="h-3 w-3" />}
            {e.sport_name}
          </span>
          {e.override_direction !== "default" && (
            <span
              data-field="override-chip"
              data-permission={e.override_permission}
              title={`${e.override_direction === "playing_up" ? "Playing up" : "Playing down"}${e.age_group_override_name ? ` to ${e.age_group_override_name}` : ""}`}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                e.override_permission === "permitted"
                  ? "bg-[#E1F5EE] text-[#085041]"
                  : e.override_permission === "conditional"
                    ? "bg-[#FAEEDA] text-[#633806]"
                    : "bg-[#FCEBEB] text-[#791F1F]"
              }`}
            >
              {e.override_direction === "playing_up" ? "Up" : "Down"}
              {e.age_group_override_name ? ` ${e.age_group_override_name}` : ""}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
